import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  TextInput,
  Stack,
  Group,
  Text,
  UnstyledButton,
  Badge,
  Loader,
  Kbd,
  Box,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconSearch,
  IconPackage,
  IconShoppingCart,
  IconFileInvoice,
  IconUsers,
} from '@tabler/icons-react';
import { useGlobalSearch, GlobalSearchResult } from '@/hooks/useApi';

interface SearchSpotlightProps {
  opened: boolean;
  onClose: () => void;
}

const TYPE_CONFIG: Record<string, { icon: typeof IconPackage; color: string; route: string }> = {
  product: { icon: IconPackage, color: 'violet', route: '/inventory/products' },
  order: { icon: IconShoppingCart, color: 'blue', route: '/sales/orders' },
  invoice: { icon: IconFileInvoice, color: 'orange', route: '/accounting/gl-entries' },
  customer: { icon: IconUsers, color: 'green', route: '/sales/customers' },
};

export default function SearchSpotlight({ opened, onClose }: SearchSpotlightProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(query, 300);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useGlobalSearch(debouncedQuery);
  const results = data?.results ?? [];

  useEffect(() => {
    if (opened) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [opened]);

  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedQuery]);

  const handleSelect = useCallback(
    (result: GlobalSearchResult) => {
      const config = TYPE_CONFIG[result.type];
      if (config) {
        navigate(config.route);
      }
      onClose();
    },
    [navigate, onClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && results.length > 0) {
        e.preventDefault();
        handleSelect(results[activeIndex]);
      }
    },
    [results, activeIndex, handleSelect],
  );

  // Group results by type
  const grouped = results.reduce<Record<string, GlobalSearchResult[]>>((acc, r) => {
    const key = r.type || 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  let flatIndex = -1;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      padding={0}
      withCloseButton={false}
      styles={{
        body: { padding: 0 },
        content: { overflow: 'hidden' },
      }}
    >
      <Box p="md" pb={0}>
        <TextInput
          ref={inputRef}
          placeholder="Search products, orders, invoices, customers..."
          leftSection={<IconSearch size={18} />}
          rightSection={<Kbd size="xs">Esc</Kbd>}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          size="md"
          variant="filled"
          aria-label="Global search"
          role="combobox"
          aria-expanded={results.length > 0}
          aria-controls="search-results"
          aria-activedescendant={results.length > 0 ? `search-result-${activeIndex}` : undefined}
        />
      </Box>

      <Box id="search-results" role="listbox" p="md" pt="sm" style={{ maxHeight: 400, overflowY: 'auto' }}>
        {query.length < 2 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            Type to search across products, orders, invoices...
          </Text>
        ) : isLoading ? (
          <Stack align="center" py="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">Searching...</Text>
          </Stack>
        ) : results.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            No results found for "{debouncedQuery}"
          </Text>
        ) : (
          <Stack gap="xs">
            {Object.entries(grouped).map(([type, items]) => {
              const config = TYPE_CONFIG[type] || { icon: IconSearch, color: 'gray', route: '/' };
              const TypeIcon = config.icon;

              return (
                <Box key={type}>
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
                    {type}s
                  </Text>
                  {items.map((result) => {
                    flatIndex++;
                    const idx = flatIndex;
                    const isActive = idx === activeIndex;

                    return (
                      <UnstyledButton
                        key={`${result.type}-${result.id}`}
                        id={`search-result-${idx}`}
                        role="option"
                        aria-selected={isActive}
                        onClick={() => handleSelect(result)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        w="100%"
                        p="xs"
                        style={(theme) => ({
                          borderRadius: theme.radius.sm,
                          backgroundColor: isActive
                            ? 'var(--mantine-color-gray-1)'
                            : 'transparent',
                        })}
                      >
                        <Group gap="sm" wrap="nowrap">
                          <TypeIcon size={18} color={`var(--mantine-color-${config.color}-6)`} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={500} lineClamp={1}>
                              {result.title}
                            </Text>
                            {result.subtitle && (
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                {result.subtitle}
                              </Text>
                            )}
                          </div>
                          <Badge size="xs" variant="light" color={config.color}>
                            {type}
                          </Badge>
                        </Group>
                      </UnstyledButton>
                    );
                  })}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    </Modal>
  );
}
